import { ParameterValidator } from '@mvps-genesis/utilities';
import EventBus from '@mvps-genesis/event-bus';
import { MessageContainer, Message } from '@mvps-genesis/messages';

export default class AjaxAddToCart {

	// define "private" properties
	_validator;

	// define "public" properties
	submitButtonTextProperty;
	submitButtonText;

	constructor(
		formElement,
		{
			submitButtonElement = '.js-add-to-cart',
			actionInputElement = 'input[name="Action"]',
			responseMessageElement = '.js-add-to-cart-message',
			processingButtonText = 'Processing...',
			successMessage = {
				message: 'Added to cart.',
				options: {
					type: 'success',
					autohide: true
				}
			},
			missingAttributesMessage = {
				message: 'All <em class="u-color-error">Required</em> options have not been selected.<br />Please review the following options: <span class="u-color-error">%missingAttributes%</span>',
				options: {
					type: 'warning',
					autohide: true
				}
			},
			limitedStockMessage = {
				message: 'We do not have enough of the combination you have selected.<br />Please adjust your quantity.',
				options: {
					type: 'warning',
					autohide: true
				}
			},
			outOfStockMessage = {
				message: 'The combination you have selected is out of stock.<br />Please review your options or check back later.',
				options: {
					type: 'warning',
					autohide: true
				}
			},
			otherMessage = {
				message: 'Please review your selection.',
				options: {
					type: 'warning',
					autohide: true
				}
			}
		} = {}
	) {

		// create validator
		this._validator = new ParameterValidator( this );
		
		// set properties from options
		this.formElement = this._validator.isElement( 'formElement', formElement );
		this.submitButtonElement = this._validator.isElement( 'options.submitButtonElement', submitButtonElement, { container: this.formElement } );
		this.actionInputElement = this._validator.isElement( 'options.actionInputElement', actionInputElement, { container: this.formElement } );
		this.processingButtonText = this._validator.isString( 'options.processingButtonText', processingButtonText );
		this.successMessage = this._validator.isObject( 'options.successMessage', successMessage );
		this.missingAttributesMessage = this._validator.isObject( 'options.missingAttributesMessage', missingAttributesMessage );
		this.limitedStockMessage = this._validator.isObject( 'options.limitedStockMessage', limitedStockMessage );
		this.outOfStockMessage = this._validator.isObject( 'options.outOfStockMessage', outOfStockMessage );
		this.otherMessage = this._validator.isObject( 'options.otherMessage', otherMessage );

		// create message container from option
		this.messageContainer = new MessageContainer( { container: this._validator.isElement( 'options.responseMessageElement', responseMessageElement, { container: this.formElement } ) } );

		// set cached / computed values
		this.submitButtonTextProperty = ( this.submitButtonElement instanceof HTMLButtonElement ) ? 'textContent' : 'value';
		this.submitButtonText = this.submitButtonElement[ this.submitButtonTextProperty ];

		// run bootstrap functions
		this.bindEvents();
		
	}

	bindEvents() {

		this.formElement.addEventListener( 'submit', this.onSubmit.bind( this ), false );

	}

	onSubmit( e ) {

		if ( this.actionInputElement.value !== 'ADPR' ) {
			return;
		}

		e.preventDefault();
		e.stopImmediatePropagation();

		// set form action / input action
		this.formElement.action = this.submitButtonElement.dataset.action;
		this.actionInputElement.value = 'ADPR';

		const formData = new FormData( this.formElement );
		const request = new XMLHttpRequest();

		// set idle status
		this.formElement.dataset.status = 'idle';

		if ( this.formElement.dataset.status !== 'processing' ) {

			// set status while processing
			this.formElement.dataset.status = 'processing';

			// disable button while processing
			this.submitButtonElement.disabled = true;

			// set text of button to processing
			this.submitButtonElement[ this.submitButtonTextProperty ] = this.processingButtonText;

			// clear response message
			this.messageContainer.clear();

			// handle readystatechange callback
			request.onreadystatechange = () => {

				// only run if the request is complete
				if ( request.readyState !== 4 ) {
					return;
				}

				// define event payload structure
				let payload = {
					success: false,
					message: null, // to be updated later in the switch statement
					data: request.response,
					formData: formData,
					request: request
				};

				// process our return data
				if ( request.status === 200 ) {

					// What do when the request is successful
					const pageCodeResponseHeader = request.getResponseHeader( 'x-mm-page-code' );

					// reset button text
					this.submitButtonElement.disabled = false;
					this.submitButtonElement[ this.submitButtonTextProperty ] = this.submitButtonText;

					switch( pageCodeResponseHeader ) {

						case 'BASK': {

							this.onSuccess();

							payload.success = true;

							break;
						}
						case 'PATR': this.onMissingAttributes(); break;
						case 'PROD': this.onMissingAttributes(); break;
						case 'PLMT': this.onLimitedStock(); break;
						case 'POUT': this.onOutOfStock(); break;
						default: this.onOther(); break;
						
					}

					// set payload message
					payload.message = this.messageContainer.currentMessage.message;

				}
				else {

					let errorMessage = `${ this.constructor.name } - The request failed with a status of: ${ request.status }`;

					// what do when the request fails
					console.error( errorMessage );
					payload.message = errorMessage;

				}

				// dispatch global event
				EventBus.dispatchEvent( 'MVPS:addToCart', payload );

				// reset form status
				this.formElement.dataset.status = 'idle';

			};


			/**
			 * Create and send a request
			 * The first argument is the post type (GET, POST, PUT, DELETE, etc.)
			 * The second argument is the endpoint URL
			 */
			request.open( this.formElement.method, this.formElement.action, true );
			request.responseType = 'document';
			request.send( formData );

		}

	}

	// message breaks
	onSuccess() {

		this.messageContainer.add( new Message( this.successMessage.message, this.successMessage.options ) );

		// re-initialize Attribute Machine (if it is active)
		if ( typeof attrMachCall !== 'undefined' ) {
			attrMachCall.Initialize();
		}

	}

	onMissingAttributes() {

		const findRequired = this.formElement.getElementsByClassName( '.is-required' );
		const missingAttributes = [];

		for ( let id = 0; id < findRequired.length; id++ ) {
			missingAttributes.push( ' ' + findRequired[id].title );
		}

		this.messageContainer.add( new Message( this.missingAttributes.message.replace( '%missingAttributes%', missingAttributes ), this.missingAttributes.options ) );

	}

	onLimitedStock() {

		this.messageContainer.add( new Message( this.limitedStockMessage.message, this.limitedStockMessage.options ) );

	}

	onOutOfStock() {

		this.messageContainer.add( new Message( this.outOfStockMessage.message, this.outOfStockMessage.options ) );

	}

	onOther() {

		this.messageContainer.add( new Message( this.otherMessage.message, this.otherMessage.options ) );

	}
	

};
